import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { BarChart3, UserCheck, Calendar, Folder, Loader2, AlertCircle } from 'lucide-react';

const SuperAdminDashboard = ({ authToken, setActiveTab }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    providerRequests: 0,
    pendingRequests: 0,
    events: 0,
    courseCategories: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError('');
      try {
        const [reqRes, eventsRes, catRes] = await Promise.all([
          fetch(`${API_BASE_URL}/admin/super/provider-requests`, { headers: { Authorization: `Bearer ${authToken}` } }),
          fetch(`${API_BASE_URL}/admin/super/events`, { headers: { Authorization: `Bearer ${authToken}` } }),
          fetch(`${API_BASE_URL}/admin/super/course-categories`, { headers: { Authorization: `Bearer ${authToken}` } }),
        ]);

        if (!reqRes.ok || !eventsRes.ok || !catRes.ok) {
          throw new Error('Failed to load dashboard data');
        }

        const [reqData, eventsData, catData] = await Promise.all([
          reqRes.json(),
          eventsRes.json(),
          catRes.json(),
        ]);

        const requests = reqData.provider_requests || [];
        const pending = requests.filter((r) => r.status === 'pending').length;

        setStats({
          providerRequests: requests.length,
          pendingRequests: pending,
          events: (eventsData.events || []).length,
          courseCategories: (catData.categories || []).length,
        });
      } catch (err) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [authToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-center text-red-700">
        <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
        {error}
      </div>
    );
  }

  const cards = [
    {
      label: 'Provider Requests',
      value: stats.providerRequests,
      sub: `${stats.pendingRequests} pending`,
      icon: UserCheck,
      tab: 'provider-requests',
      color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    },
    {
      label: 'Events & Exhibitions',
      value: stats.events,
      icon: Calendar,
      tab: 'events',
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    },
    {
      label: 'Course Categories',
      value: stats.courseCategories,
      icon: Folder,
      tab: 'course-categories',
      color: 'bg-amber-50 text-amber-700 border-amber-200',
    },
  ];

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Super Admin Dashboard</h2>
      <p className="text-gray-600 mb-6">Platform overview and key stats.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.tab}
              type="button"
              onClick={() => setActiveTab && setActiveTab(card.tab)}
              className={`p-4 rounded-lg border text-left transition hover:shadow-md ${card.color}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium opacity-90">{card.label}</p>
                  <p className="text-2xl font-bold mt-1">{card.value}</p>
                  {card.sub && <p className="text-xs mt-1 opacity-80">{card.sub}</p>}
                </div>
                <Icon className="h-8 w-8 opacity-80" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
